import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Input from '../../../components/ui/Input';
import Textarea from '../../../components/ui/Textarea';
import Button from '../../../components/ui/Button';
import CamperPreview from './CamperPreview';
import SafeIcon from '../../../common/SafeIcon';
import { api } from '../../../api/nocode';
import { sendEmail } from '../../../api/ses';
import { useAuth } from '../../../context/AuthContext';

export default function AssemblyEditor({ data, onSave, onHistory }) {
  const { user } = useAuth();
  const [form, setForm] = useState(data || {});
  const [showPreview, setShowPreview] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState(user?.email || '');
  const [sendProgress, setSendProgress] = useState({ current: 0, total: 0 });

  useEffect(() => setForm(data || {}), [data]);
  useEffect(() => {
    if (user?.email && !testEmail) setTestEmail(user.email);
  }, [user]);

  const handleChange = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const parsedBonusVideos = () => {
    try {
      const parsed = JSON.parse(form.bonus_videos || '[]');
      if (Array.isArray(parsed)) {
        // Backward compatibility for when it was just an array of strings
        return parsed.map(v => typeof v === 'string' ? { url: v, embed: '', description: '' } : v);
      }
      return [];
    } catch {
      return [];
    }
  };

  const bonusVideos = parsedBonusVideos();

  const handleBonusVideoChange = (index, field, value) => {
    const newVideos = [...bonusVideos];
    newVideos[index] = { ...newVideos[index], [field]: value };
    handleChange('bonus_videos', JSON.stringify(newVideos));
  };

  const addBonusVideo = () => {
    handleChange('bonus_videos', JSON.stringify([...bonusVideos, { url: '', embed: '', description: '' }]));
  };

  const removeBonusVideo = (index) => {
    const newVideos = [...bonusVideos];
    newVideos.splice(index, 1);
    handleChange('bonus_videos', JSON.stringify(newVideos));
  };

  const generateBrandedHtml = (subject, body) => {
    return `
      <div style="font-family: 'Helvetica Neue', Helvetica, Arial,sans-serif; max-width: 600px; margin: 0 auto; background-color: #fdfaf6; padding: 0;">
        <div style="text-align: center; padding: 40px 20px 20px;">
          <img src="https://www.faibowrimo.com/wp-content/uploads/2026/05/camp-logo-sm.png" alt="Camp FaiBoWriMo" style="max-width: 180px; height: auto;" />
        </div>
        <div style="padding: 0 40px 20px;">
          <h2 style="color: #2D5016; margin-bottom: 25px; font-size: 24px; text-align: center;">${subject}</h2>
          
          <div style="font-size: 17px; line-height: 1.8; background: #ffffff; padding: 40px; border-top: 4px solid #D4A373; border-radius: 8px; color: #2c3e20; box-shadow: 0 4px 15px rgba(0,0,0,0.03);">
${body}
          </div>
          
          <p style="margin-top: 40px; text-align: center; font-size: 12px; color: #a39b90;">
            © 2026 Gospel-Wise Author. All rights reserved.
          </p>
        </div>
      </div>
    `;
  };

  const handleTestSend = async () => {
    if (!testEmail) return alert("Enter a test email address first.");
    const isNoteEmpty = !form.counselor_note || form.counselor_note.trim() === '' || form.counselor_note === '<p><br></p>';
    if (!form.counselor_subject || isNoteEmpty) {
      alert("Please enter a subject and body for the counselor note before sending.");
      return;
    }

    setIsSendingTest(true);
    try {
      const htmlBody = generateBrandedHtml(form.counselor_subject, form.counselor_note);
      const success = await sendEmail(testEmail, `[TEST] ${form.counselor_subject}`, htmlBody);
      if (success) {
        alert(`Test email successfully delivered to ${testEmail}!`);
      } else {
        alert("Failed to send test email. Check console for errors.");
      }
    } catch (err) {
      console.error("Test send error:", err);
      alert("Error sending test email.");
    } finally {
      setIsSendingTest(false);
    }
  };

  const handleSendToAllAccess = async () => {
    const isNoteEmpty = !form.counselor_note || form.counselor_note.trim() === '' || form.counselor_note === '<p><br></p>';
    if (!form.counselor_subject || isNoteEmpty) {
      alert("Please enter a subject and body for the counselor note before sending.");
      return;
    }

    const confirmed = window.confirm("This will send the Cabin Counselor Note to all All-Access campers right now. Are you sure?");
    if (!confirmed) return;

    setIsSending(true);
    setSendProgress({ current: 0, total: 0 });

    try {
      const res = await api.get('users_gwacamp', { pass_type: 'allaccess' });
      const users = Array.isArray(res) ? res : (res?.data || []);
      
      if (users.length === 0) {
        alert("No all-access campers found.");
        setIsSending(false);
        return;
      }

      setSendProgress({ current: 0, total: users.length });

      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < users.length; i++) {
        const user = users[i];
        if (!user.email) {
          failCount++;
          setSendProgress(prev => ({ ...prev, current: i + 1 }));
          continue;
        }

        const subject = form.counselor_subject;
        const htmlBody = generateBrandedHtml(subject, form.counselor_note);

        const success = await sendEmail(user.email, subject, htmlBody);
        if (success) {
          successCount++;
        } else {
          failCount++;
        }

        setSendProgress({ current: i + 1, total: users.length });
      }

      alert(`Sent to ${successCount} campers successfully.${failCount > 0 ? ` ${failCount} failed.` : ''}`);
      
      const now = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });
      const updatedForm = { ...form, counselor_last_sent_at: now };
      handleChange('counselor_last_sent_at', now);
      onSave(updatedForm);

    } catch (error) {
      console.error("Error sending counselor notes:", error);
      alert("An error occurred while fetching users or sending emails. Check console for details.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      <div className="flex-1 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <Input 
            label="Day title" 
            value={form.day_title || ''} 
            onChange={e => handleChange('day_title', e.target.value)} 
            placeholder="e.g. Mindset + Calling Clarity"
          />
          <Input 
            label="Persona label" 
            value={form.persona_label || ''} 
            onChange={e => handleChange('persona_label', e.target.value)} 
            placeholder="e.g. The Christian"
          />
        </div>

        <div className="flex items-center gap-4">
          <Input 
            type="color" 
            label="Day badge color" 
            value={form.badge_color || '#2D5016'} 
            onChange={e => handleChange('badge_color', e.target.value)} 
            className="w-32"
          />
          
          <button 
            type="button"
            className="flex items-center gap-3 cursor-pointer p-2 bg-gray-50 border rounded-md hover:bg-gray-100 flex-1 mt-6"
            onClick={() => handleChange('is_active', form.is_active ? 0 : 1)}
          >
            <div className={`w-10 h-6 rounded-full relative transition-colors ${form.is_active ? 'bg-green-500' : 'bg-gray-300'}`}>
              <div className={`w-4 h-4 rounded-full bg-white absolute top-1 transition-all ${form.is_active ? 'left-5' : 'left-1'}`}></div>
            </div>
            <span className="font-bold text-gray-700">Block is Active</span>
          </button>
        </div>

        <Textarea 
          label="Intro paragraph" 
          value={form.intro_text || ''} 
          onChange={e => handleChange('intro_text', e.target.value)} 
          className="min-h-[100px]"
        />

        <div className="grid md:grid-cols-2 gap-4">
          <Input 
            label="Video Embed URL (Gumlet, Livid, YouTube, etc.)" 
            value={form.video_url || ''} 
            onChange={e => handleChange('video_url', e.target.value)} 
            placeholder="e.g. https://livid.com/watch/..."
          />
          <Textarea 
            label="Or paste Iframe Embed Code" 
            value={form.video_embed || ''} 
            onChange={e => handleChange('video_embed', e.target.value)} 
            placeholder='<iframe src="..." ...></iframe>'
            rows={3}
          />
        </div>

        <Input 
          label="Video thumbnail URL (Optional)" 
          value={form.thumbnail_url || ''} 
          onChange={e => handleChange('thumbnail_url', e.target.value)} 
        />

        <div className="pt-4 border-t border-gray-200 mt-6 space-y-4">
          <h3 className="font-bold text-dark-green text-lg">Featured Product (Optional)</h3>
          <Input 
            label="Product Headline (e.g., product name and price)" 
            value={form.product_headline || ''} 
            onChange={e => handleChange('product_headline', e.target.value)} 
            placeholder="e.g. GospelWise Mug — Only $10!"
          />
          <Input 
            label="Product Image URL" 
            value={form.product_image_url || ''} 
            onChange={e => handleChange('product_image_url', e.target.value)} 
            placeholder="https://example.com/image.png"
          />
          <Textarea 
            label="Product Short Description (Optional)" 
            value={form.product_description || ''} 
            onChange={e => handleChange('product_description', e.target.value)} 
            placeholder="Introduce this super discounted item in a few words..."
            rows={2}
          />
          <div className="grid md:grid-cols-2 gap-4">
            <Input 
              label="Product Link URL" 
              value={form.product_url || ''} 
              onChange={e => handleChange('product_url', e.target.value)} 
              placeholder="https://example.com/product"
            />
            <Input 
              label="Button Text" 
              value={form.product_button_text || ''} 
              onChange={e => handleChange('product_button_text', e.target.value)} 
              placeholder="e.g. Grab It Now"
            />
          </div>
        </div>

        <div className="pt-4 border-t border-gray-200 mt-6 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-dark-green text-lg">Bonus Videos</h3>
            <Button type="button" variant="outline" size="sm" onClick={addBonusVideo}>
              + Add Bonus Video
            </Button>
          </div>
          
          {bonusVideos.map((video, i) => {
            return (
              <div key={i} className="flex gap-4 items-start p-4 bg-gray-50 rounded border border-gray-200">
                <div className="flex-1 space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input 
                      label={`Bonus Video Embed URL ${i + 1}`} 
                      value={video.url || ''} 
                      onChange={e => handleBonusVideoChange(i, 'url', e.target.value)} 
                      placeholder="https://livid.com/watch/..."
                    />
                    <Textarea 
                      label="Or paste Iframe Embed Code" 
                      value={video.embed || ''} 
                      onChange={e => handleBonusVideoChange(i, 'embed', e.target.value)} 
                      placeholder='<iframe src="..." ...></iframe>'
                      rows={3}
                    />
                  </div>
                  <Textarea 
                    label="Description (Optional)" 
                    value={video.description || ''} 
                    onChange={e => handleBonusVideoChange(i, 'description', e.target.value)} 
                    placeholder="Enter a brief context or description for this bonus video..."
                    rows={2}
                  />
                </div>
                <button 
                  type="button"
                  onClick={() => removeBonusVideo(i)}
                  className="mt-8 p-2 text-red-500 hover:bg-red-50 rounded border border-transparent hover:border-red-200"
                >
                  <SafeIcon name="Trash2" size={20} />
                </button>
              </div>
            );
          })}
          {bonusVideos.length === 0 && (
            <p className="text-sm text-gray-500 italic">No bonus videos added yet.</p>
          )}
        </div>

        <Input 
          type="datetime-local" 
          label="Unlock Date & Time" 
          value={form.unlock_time || ''} 
          onChange={e => handleChange('unlock_time', e.target.value)} 
        />

        <div className="pt-4 border-t border-gray-200 mt-6 space-y-4">
          <h3 className="font-bold text-dark-green text-lg">Cabin Counselor Note (All-Access Only)</h3>
          
          <Input 
            label="Email Subject Line" 
            value={form.counselor_subject || ''} 
            onChange={e => handleChange('counselor_subject', e.target.value)} 
            placeholder="e.g. Day 1: Welcome to Camp!"
          />
          
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-dark-green mb-1">Email Body</label>
            <div className="bg-white rounded-md border border-amber overflow-hidden">
              <ReactQuill 
                theme="snow" 
                value={form.counselor_note || ''} 
                onChange={val => handleChange('counselor_note', val)} 
                className="h-64 mb-12"
              />
            </div>
            <div className="text-xs text-gray-500 text-right mt-1">
              Provides daily encouragement directly to camper inboxes.
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 pt-4 border-t border-gray-200 mt-6 bg-gray-50 p-6 rounded-lg">
          <h4 className="font-bold text-dark-green mb-2 flex items-center gap-2">
            <SafeIcon name="Send" size={18} /> Send Controls
          </h4>
          
          <div className="flex flex-col sm:flex-row items-end gap-3 mb-4 p-4 bg-white border rounded-md shadow-sm">
            <div className="flex-1 w-full">
              <Input 
                label="Admin Test Email"
                value={testEmail}
                onChange={e => setTestEmail(e.target.value)}
                placeholder="Enter email for test..."
              />
            </div>
            <Button 
              variant="outline" 
              className="bg-gray-800 text-white hover:bg-black shrink-0 h-10 mb-1"
              onClick={handleTestSend}
              disabled={isSendingTest}
            >
              {isSendingTest ? 'Sending...' : 'Send Test'}
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => {
              if (form.is_active && !form.video_url && !form.video_embed) {
                alert("Please provide either a Video Embed URL or an Iframe Embed Code.");
                return;
              }
              onSave(form);
            }}>Save Assembly</Button>
            <Button 
              variant="outline" 
              className="border-2 border-forest-green text-forest-green bg-white hover:bg-cream"
              onClick={handleSendToAllAccess}
              disabled={isSending}
            >
              {isSending ? (
                 `Sending to ${sendProgress.current} of ${sendProgress.total} campers...`
              ) : (
                <span className="flex items-center gap-2">
                  <SafeIcon name="Users" size={16} /> Send to All-Access Campers Now
                </span>
              )}
            </Button>
            <Button variant="ghost" onClick={onHistory}>View History</Button>
          </div>
          {form.counselor_last_sent_at && (
            <div className="text-sm font-medium text-gray-500 mt-1">
              Last sent to All-Access: {form.counselor_last_sent_at}
            </div>
          )}
        </div>
      </div>

      <div className={`w-full lg:w-[400px] ${showPreview ? 'block' : 'hidden lg:block'}`}>
        <CamperPreview type="assembly" data={form} />
      </div>

      <button 
        className="lg:hidden w-full py-2 bg-gray-100 text-sm font-bold text-gray-600 rounded mt-4" 
        onClick={() => setShowPreview(!showPreview)}
      >
        {showPreview ? 'Hide Preview' : 'Show Preview'}
      </button>
    </div>
  );
}